import { Component, OnInit, OnDestroy, ViewChildren, QueryList } from '@angular/core';
import { FormBuilder, FormGroup } from "@angular/forms";
import { Subscription, Observable, BehaviorSubject, combineLatest, of } from "rxjs";
import { map, tap } from "rxjs/operators";
import { GridOptions, ColDef, GridApi, SelectionColumnDef, RowSelectionOptions } from "ag-grid-community";


import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { IssuesService, IssueSearchSpec, IssueSearchResult } from "../../../common/services/issues.service";
import { IssueType } from "../../../common/models/issue.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Option } from "../../../common/models/flow.dto";
import { OrganizationStub } from "../../../common/models/organization.model";
import { OrganizationService } from "../../../common/services/organization.service";
import { CommonDatePickerComponent } from "../../../common/components/common-date-picker/common-date-picker.component";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { AgGridBooleanFilterComponent } from "../../../common/components/ag-grid/filters/boolean-filter.component";
import { Dialog, DialogButton } from '../../../core/models/dialog.model';

@Component({
    selector: 'app-issues-search',
    templateUrl: './issues-search.component.html',
    styleUrls: ['./issues-search.component.scss'],
    standalone: false
})
export class IssuesSearchComponent implements OnInit, OnDestroy {

    applicableTypes: string[] = ["AD", "ADLD", "ADS", "BDS", "F8", "F8A", "F8B", "F8C", "F8F", "LOGD", "INS", "FS", "FA", "F13", "IFEE", "PDUD", "PDUE", "PDUF", "C2AR-ERP", "C2AR-SWIW", "SMQR", "SMSR"];

    @ViewChildren(CommonDatePickerComponent) datePickers: QueryList<CommonDatePickerComponent>;

    data: Observable<IssueSearchResult[]>;

    form: FormGroup;
    formErrors: string[] = [];
    issueSearchRun: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    loadedOrganizations: OrganizationStub[] = [];
    organization?: OrganizationStub | null;

    orgNotFound: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    orgSearchActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);


    programAreas: Option[] = [
        { id: "Surety", text: "Surety" } as Option,
        { id: "Permitting", text: "Permitting" } as Option,
        { id: "Production", text: "Production" } as Option,
        { id: "UIC", text: "UIC" } as Option
    ];

    searchingIssues: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    subheader: SubHeader = new SubHeader({ title: "Search Issues" });
    subscription: Subscription = new Subscription();

    types: IssueType[];

    gridOptions: GridOptions;
    gridApi?: GridApi;
    apiColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "API #",
        headerTooltip: "API #",
        field: "apiNumber",
        valueGetter: this.formatter.formatPotentiallyBlankField("apiNumber", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("apiNumber", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 125,
        maxWidth: 150,
        cellClass: "stringType"
    } as ColDef;

    closedDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Resolved Date",
        headerTooltip: "Resolved Date",
        field: "dateResolved",
        tooltipValueGetter: this.formatter.formatDate("dateResolved"),
        valueGetter: this.formatter.formatDate("dateResolved"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145
    } as ColDef;

    contestedColumn: ColDef = {
        headerName: "Contested?",
        headerTooltip: "Contested?",
        field: "contested",
        tooltipValueGetter: this.formatter.formatBooleanToYesNo("contested"),
        valueGetter: this.formatter.formatBooleanToYesNo("contested"),
        minWidth: 150,
        maxWidth: 150
    } as ColDef;


    createdDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Date Created",
        headerTooltip: "Date Created",
        field: "dateCreated",
        tooltipValueGetter: this.formatter.formatDate("dateCreated"),
        valueGetter: this.formatter.formatDate("dateCreated"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145
    } as ColDef;

    descriptionColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Description",
        headerTooltip: "Issue Description",
        field: "issueDescription",
        tooltipField: "issueDescription",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
        maxWidth: 150
    } as ColDef;

    dueDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Due Date",
        headerTooltip: "Due Date",
        field: "dateDue",
        tooltipValueGetter: this.formatter.formatDate("dateDue"),
        valueGetter: this.formatter.formatDate("dateDue"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145,
    } as ColDef;

    extensionGrantedColumn: ColDef = {
        headerName: "Extension Granted?",
        headerTooltip: "Extension Granted?",
        field: "extensionGranted",
        tooltipValueGetter: this.formatter.formatBooleanToYesNo("extensionGranted"),
        valueGetter: this.formatter.formatBooleanToYesNo("extensionGranted"),
        minWidth: 150,
        maxWidth: 150
    } as ColDef;

    issueTypeColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Issue Code",
        headerTooltip: "Issue Code",
        field: "issueCode",
        tooltipField: "issueCode",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 120,
    } as ColDef;

    legacyOwnerNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Legacy Owner Number",
        headerTooltip: "Legacy Owner Number",
        field: "legacyOwnerNumber",
        filter: "agTextColumnFilter",
        tooltipField: "legacyOwnerNumber",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    nextActionDateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Next Action Date",
        headerTooltip: "Next Action Date",
        field: "nextActionDate",
        tooltipValueGetter: this.formatter.formatDate("nextActionDate"),
        valueGetter: this.formatter.formatDate("nextActionDate"),
        filter: "agDateColumnFilter",
        filterParams: this.formatter.defaultDateFilterParams,
        minWidth: 145,
        maxWidth: 145
    } as ColDef;

    organizationAddressColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Organization Address",
        headerTooltip: "Organization Address",
        field: "organizationAddress",
        tooltipField: "organizationAddress",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    organizationCityColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Organization City",
        headerTooltip: "Organization City",
        field: "organizationCity",
        tooltipField: "organizationCity",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    organizationPoBoxColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Organization PO Box",
        headerTooltip: "Organization PO Box",
        field: "organizationPOBox",
        tooltipField: "organizationPOBox",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    organizationStateColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Organization State",
        headerTooltip: "Organization State",
        field: "organizationState",
        tooltipField: "organizationState",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    organizationSuiteColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Organization Suite",
        headerTooltip: "Organization Suite",
        field: "organizationSuite",
        tooltipField: "organizationSuite",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    organizationZipCodeColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Organization Zip Code",
        headerTooltip: "Organization Zip Code",
        field: "organizationZipCode",
        tooltipField: "organizationZipCode",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    ownerNameColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Organization Name",
        headerTooltip: "Organization Name",
        field: "ownerName",
        valueGetter: this.formatter.formatPotentiallyBlankField("organizationName", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("organizationName", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    ownerNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Owner Number",
        headerTooltip: "Owner Number",
        field: "ownerNumber",
        valueGetter: this.formatter.formatPotentiallyBlankField("ownerNumber", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("ownerNumber", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
    } as ColDef;

    primaryContactEmailColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Primary Contact Email",
        headerTooltip: "Primary Contact Email",
        field: "primaryContactEmail",
        tooltipField: "primaryContactEmail",
        valueGetter: this.formatter.formatPotentiallyBlankField("primaryContactEmail", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("primaryContactEmail", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
        maxWidth: 150
    } as ColDef;

    primaryContactNameColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Primary Contact Name",
        headerTooltip: "Primary Contact Name",
        field: "primaryContactName",
        tooltipField: "primaryContactName",
        valueGetter: this.formatter.formatPotentiallyBlankField("primaryContactName", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("primaryContactName", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150,
        maxWidth: 150
    } as ColDef;

    programAreaColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Program Area",
        headerTooltip: "Program Area",
        field: "programArea",
        tooltipField: "programArea",
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 150
    } as ColDef;

    selectionColumnDef: SelectionColumnDef = {
        headerClass: "wrapped",
        suppressAutoSize: true,
        suppressSizeToFit: true,
        maxWidth: 75,
        minWidth: 75,
        filter: null,
        sortable: false
    } as SelectionColumnDef;

    wellNameColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Well Name",
        headerTooltip: "Well Name",
        field: "wellName",      
        filter: "agTextColumnFilter",
        valueGetter: this.formatter.formatPotentiallyBlankField("wellName", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("wellName", "N/A"),
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 125,
    } as ColDef;

    wellNumberColumn: ColDef = {
        headerClass: "wrapped",
        headerName: "Well Number",
        headerTooltip: "Well Number",
        field: "wellNumber",       
        valueGetter: this.formatter.formatPotentiallyBlankField("wellNumber", "N/A"),
        tooltipValueGetter: this.formatter.formatPotentiallyBlankField("wellNumber", "N/A"),
        filter: "agTextColumnFilter",
        filterParams: this.formatter.defaultTextFilterParams,
        minWidth: 125,
    } as ColDef;

    constructor(private builder: FormBuilder, private subheaderService: SubHeaderService, private issuesService: IssuesService, private dialogService: DynamicDialogService,
        private organizationService: OrganizationService, private formatter: GridFormatterService)
    { }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.subheader);
        this.form = this.buildForm();
        const typeSubs$ = this.issuesService.getAllIssueTypes().pipe(
            map((data: IssueType[]) => {
                return data.filter(d => this.applicableTypes.findIndex(i => d.id === i) !== -1);
            })
        ).subscribe({
            next: (typeList: IssueType[]) => { this.types = typeList; },
            error: (error) => { this.handleError(error); }
        });
        this.subscription.add(typeSubs$);
        this.defineGridOptions();
    }


    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    buildForm(): FormGroup {
        return this.builder.group({
            programArea: ["", []],
            issueType: ["", []],
            ownerNumber: ["", []],
            legacyOwnerNumber: ["", []],
            createdFromDate: ["", []],
            createdToDate: ["", []],
            dueFromDate: ["", []],
            dueToDate: ["", []],
            resolutionFromDate: ["", []],
            resolutionToDate: ["", []],
            yearFrom: ["", []],
            yearTo: ["", []],
            quarter1: [false, []],
            quarter2: [false, []],
            quarter3: [false, []],
            quarter4: [false, []]
        });
    }

    convertFromDate(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    }

    convertToDate(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59);
    }

    createdFromChanged(date: Date): void {
        const ctrl = this.form.get("createdFromDate");
        if (ctrl != null) {
            if (date != null) {
                ctrl.setValue(this.convertFromDate(date));
                ctrl.markAsDirty();
            }
            else {
                ctrl.setValue("");
                ctrl.markAsPristine();
            }
        }
    }

    createdToChanged(date: Date): void {
        const ctrl = this.form.get("createdToDate");
        if (ctrl != null) {
            if (date != null) {
                ctrl.setValue(this.convertFromDate(date));
                ctrl.markAsDirty();
            }
            else {
                ctrl.setValue("");
                ctrl.markAsPristine();
            }
        }
    }

    defineGridOptions(): void {
        const columns: ColDef[] = [];
        // columns.push(this.selectionColumn);
        columns.push(this.createdDateColumn);
        columns.push(this.dueDateColumn);
        columns.push(this.closedDateColumn);
        columns.push(this.programAreaColumn);
        columns.push(this.issueTypeColumn);
        columns.push(this.descriptionColumn);
        columns.push(this.ownerNameColumn);
        columns.push(this.ownerNumberColumn);
        columns.push(this.legacyOwnerNumberColumn);
        columns.push(this.apiColumn);
        columns.push(this.wellNameColumn);
        columns.push(this.wellNumberColumn);
        columns.push(this.nextActionDateColumn);
        columns.push(this.extensionGrantedColumn);
        columns.push(this.contestedColumn);
        columns.push(this.primaryContactNameColumn);
        columns.push(this.primaryContactEmailColumn);
        columns.push(this.organizationAddressColumn);
        columns.push(this.organizationSuiteColumn);
        columns.push(this.organizationPoBoxColumn);
        columns.push(this.organizationCityColumn);
        columns.push(this.organizationStateColumn);
        columns.push(this.organizationZipCodeColumn);
        this.gridOptions = this.formatter.BuildDefaultGridOptionsWithColumns(columns, false);
        this.gridOptions.rowSelection = {
            mode: 'multiRow'
        } as RowSelectionOptions;
        this.gridOptions.onGridReady = (params) => {
            this.gridApi = params.api;
        };
    }

    dueFromChanged(date: Date): void {
        const ctrl = this.form.get("dueFromDate");
        if (ctrl != null) {
            if (date != null) {
                ctrl.setValue(this.convertFromDate(date));
                ctrl.markAsDirty();
            }
            else {
                ctrl.setValue("");
                ctrl.markAsPristine();
            }
        }
    }

    dueToChanged(date: Date): void {
        const ctrl = this.form.get("dueToDate");
        if (ctrl != null) {
            if (date != null) {
                ctrl.setValue(this.convertFromDate(date));
                ctrl.markAsDirty();
            }
            else {
                ctrl.setValue("");
                ctrl.markAsPristine();
            }
        }
    }

    export(): void {
        if (this.gridApi) {
            const fileName = `OpenIssues_${(new Date().toLocaleString())}`;
            this.formatter.exportDataToExcel(fileName, "Issues", this.gridApi, true);
        }
    }

    handleError(err: any): void {
        console.error(err);
        this.orgSearchActive.next(false);
        this.searchingIssues.next(false);
        this.dialogService.showError();
    }

    handleOwnerNotFoundError(message: string): void {
        this.orgSearchActive.next(false);
        this.searchingIssues.next(false);
        const dialog = new Dialog(message, [new DialogButton("OK", false, () => { return; })]);
        dialog.toggleDialog(true);
        this.dialogService.updateDialog(dialog);
    }

    lookupOwner(idType: string): void {
        const ctrl = (idType === 'rics') ? this.form.get("ownerNumber") : this.form.get("legacyOwnerNumber");
        if (ctrl != null) {
            this.orgNotFound.next(false);
            this.orgSearchActive.next(true);
            const storedOrg = (idType === 'rics')
                ? this.loadedOrganizations.find(o => o.id === ctrl.value)
                : this.loadedOrganizations.find(o => o.historicalOwnerNumber === ctrl.value);

            let obs$: Observable<OrganizationStub>;
            if (storedOrg != null) {
                obs$ = of(storedOrg);
            } else {
                obs$ = (idType === "rics")
                    ? this.organizationService.getOrganizationStub(ctrl.value)
                    : this.organizationService.getOrganizationStubByHistoricalOwnerNumber(ctrl.value);
            }
            const orgSub$ = obs$.pipe(
                tap((org: OrganizationStub) => {
                    this.organization = org;
                    
                })
            ).subscribe({
                next: (org: OrganizationStub) => {
                    this.orgNotFound.next(org == null);
                    this.orgSearchActive.next(false);
                    if (org != null) {
                        const ownerCtrl = this.form.get("ownerNumber");
                        if (ownerCtrl != null) ownerCtrl.setValue(org.id);
                        const legacyCtrl = this.form.get("legacyOwnerNumber");
                        if (legacyCtrl != null) legacyCtrl.setValue(org.historicalOwnerNumber);
                        this.loadedOrganizations.push(org);
                    }
                },
                error: (error) => { 
                    if (error.status === 404) {
                        this.handleOwnerNotFoundError("Owner not found."); 
                    } else {
                        this.handleError(error);
                    }
                }
            });
            this.subscription.add(orgSub$);
        }
        
    }

    reset(): void {
        this.organization = null;
        this.datePickers.forEach(dp => {
            dp.formGroup.reset();
        });
        this.form.reset();
        this.formErrors = [];
        this.issueSearchRun.next(false);
        this.orgNotFound.next(false);
    }

    resolvedFromChanged(date: Date): void {
        const ctrl = this.form.get("resolutionFromDate");
        if (ctrl != null) {
            if (date != null) {
                ctrl.setValue(this.convertFromDate(date));
                ctrl.markAsDirty();
            }
            else {
                ctrl.setValue("");
                ctrl.markAsPristine();
            }
        }
    }

    resolvedToChanged(date: Date): void {
        const ctrl = this.form.get("resolutionToDate");
        if (ctrl != null) {
            if (date != null) {
                ctrl.setValue(this.convertFromDate(date));
                ctrl.markAsDirty();
            }
            else {
                ctrl.setValue("");
                ctrl.markAsPristine();
            }
        }
    }

    search(spec: IssueSearchSpec): Observable<IssueSearchResult[]> {
        return this.issuesService.searchIssues(spec).pipe(
            tap((results) => { this.searchingIssues.next(false); })
        );
    }


    searchIssues(): void {
        this.formErrors = [];
        if (this.form.pristine) {
            this.formErrors.push("Please enter at least one search criteria.");
        }
        const areaCtrl = this.form.get("programArea");
        const typeCtrl = this.form.get("issueType");
        const ownerCtrl = this.form.get("ownerNumber");
        const legacyCtrl = this.form.get("legacyOwnerNumber");
        const createdFromCtrl = this.form.get("createdFromDate");
        const createdToCtrl = this.form.get("createdToDate");
        let createdFrom: Date | null = null;
        let createdTo: Date | null = null;
        if (createdFromCtrl != null && createdToCtrl != null) {
            if (createdFromCtrl.value != null && createdFromCtrl.value !== "") createdFrom = createdFromCtrl.value;
            if (createdToCtrl.value != null && createdToCtrl.value !== "") createdTo = createdToCtrl.value;
            if (createdFrom != null && createdTo !== null && createdTo < createdFrom) this.formErrors.push("Created Date From must be less than Created Date To");            
        }
        const dueFromCtrl = this.form.get("dueFromDate");
        const dueToCtrl = this.form.get("dueToDate");
        let dueFrom: Date = null;
        let dueTo: Date = null;
        if (dueFromCtrl != null && createdToCtrl != null) {
            if (dueFromCtrl.value != null && dueFromCtrl.value !== "") dueFrom = dueFromCtrl.value;
            if (dueToCtrl.value != null && dueToCtrl.value !== "") dueTo = dueToCtrl.value;
            if (dueTo !== null && dueTo < dueFrom) this.formErrors.push("Due Date From must be less than Due Date To");
        }
        const resolvedFromCtrl = this.form.get("resolutionFromDate");
        const resolvedToCtrl = this.form.get("resolutionToDate");
        let resolvedFrom: Date = null;
        let resolvedTo: Date = null;
        if (resolvedFromCtrl != null && resolvedToCtrl != null) {
            if (resolvedFromCtrl.value != null && resolvedFromCtrl.value !== "") resolvedFrom = resolvedFromCtrl.value;
            if (resolvedToCtrl.value != null && resolvedToCtrl.value !== "") resolvedTo = resolvedToCtrl.value;
            if (resolvedTo !== null && resolvedTo < resolvedFrom) this.formErrors.push("Resolution Date From must be less than Resolution Date To");
        }
        let yearFrom: number = null;
        let yearTo: number = null;
        const yearFromCtrl = this.form.get("yearFrom");
        const yearToCtrl = this.form.get("yearTo");
        if (yearFromCtrl != null && yearToCtrl != null) {
            if (yearFromCtrl.value != null && yearFromCtrl.value !== "") yearFrom = parseInt(yearFromCtrl.value, 10);
            if (yearToCtrl.value != null && yearToCtrl.value !== "") yearTo = parseInt(yearToCtrl.value, 10);
            if (yearTo == null && yearFrom != null) this.formErrors.push("When using Year From, a Year To must also be used.");
            if (yearFrom == null && yearTo != null) this.formErrors.push("When using Year To, a Year From must also be used.");
            if (yearTo < yearFrom) this.formErrors.push("Year From must be less than or equal to Year To");
        }
        const quarters: string[] = [];
        const qtr1Ctrl = this.form.get("quarter1");
        if (qtr1Ctrl != null && qtr1Ctrl.value === true) {
            quarters.push("Q1");
        }
        const qtr2Ctrl = this.form.get("quarter2");
        if (qtr2Ctrl != null && qtr2Ctrl.value === true) {
            quarters.push("Q2");
        }
        const qtr3Ctrl = this.form.get("quarter3");
        if (qtr3Ctrl != null && qtr3Ctrl.value === true) {
            quarters.push("Q3");
        }
        const qtr4Ctrl = this.form.get("quarter4");
        if (qtr4Ctrl != null && qtr4Ctrl.value === true) {
            quarters.push("Q4");
        }
        if (this.formErrors.length === 0) {
            const spec = {
                issueOwner: (areaCtrl != null && areaCtrl.value != null && areaCtrl.value.length > 0) ? areaCtrl.value : "",
                typeCodes: (typeCtrl != null && typeCtrl.value != null && typeCtrl.value.length > 0) ? [typeCtrl.value] : [],
                organizationIds: (ownerCtrl != null && ownerCtrl.value != null && ownerCtrl.value.length > 0) ? [ownerCtrl.value] : [],
                legacyIds: (legacyCtrl != null && legacyCtrl.value != null && legacyCtrl.value.length > 0) ? [legacyCtrl.value] : [],
                createdDateFrom: createdFrom,
                createdDateTo: createdTo,
                dueDateFrom: dueFrom,
                dueDateTo: dueTo,
                resolvedDateFrom: resolvedFrom,
                resolvedDateTo: resolvedTo,
                loadChildren: true,
                loadContactDetails: true,
                loadOrganizationDetails: true,
                loadWellDetails: true,
                yearFrom: yearFrom,
                yearTo: yearTo,
                quarters: quarters
            } as IssueSearchSpec;
            this.searchingIssues.next(true);
            this.issueSearchRun.next(true);
           
            this.data = this.search(spec).pipe(
                tap((issues: IssueSearchResult[]) => {
                })
            );
        }
      
    }

    get excelStyles(): any[] {
        if (this.formatter) return this.formatter.excelStyles;
        return [];
    }
}
